import { z } from "zod"
import {
  numberOfPaymentsValues,
  paymentMethodsValues,
} from "src/features/calculator/utils/calculator"

export const calculationSchema = z.object({
  applicationId: z.string().min(1, "必須入力です"),
  userId: z.string().min(1, "必須入力です"),
  userName: z.string().min(1, "必須入力です"),
  storeId: z.optional(z.string()),
  storeName: z.optional(z.string()),
  adPrice: z.optional(z.coerce.number().min(0, "0以上の値を入力してください")),
  isRecipient: z.optional(
    // select fieldでbooleanを使用できないのでstringにしている
    z.enum(["true", "false"], {
      required_error: "必須入力です",
      invalid_type_error: "無効な値です",
    })
  ),
  nop: z.optional(
    z
      .number()
      .refine((value) => value !== undefined && value !== null, {
        message: "必須入力です",
      })
      .refine((num) => numberOfPaymentsValues.includes(num), {
        message: `分割回数は${numberOfPaymentsValues.join(", ")}の内のどれかでなければいけません`,
      })
  ),
  scheduledDealAt: z.string().min(1, "必須入力です"),
  paymentMethod: z
    .number()
    .refine((value) => value !== undefined && value !== null, {
      message: "必須入力です",
    })
    .refine((num) => paymentMethodsValues.includes(num), {
      message: `支払い方法は${paymentMethodsValues.join(", ")}の内のどれかでなければいけません`,
    }),
  applicatedAt: z.string().min(1, "必須入力です"),
  strategy: z.string().min(1, "必須入力です"),
  exCustomerTransferFeeRate: z.optional(z.number()), // 送客手数料率(税抜)
  consumptionTaxRate: z.optional(z.number()), // 消費税率
  maxAdvancePayment: z.number(), // 貸付上限額
  initialCost: z.number(), // 初期費用総額
  downPayment: z.number(), // 頭金
  rent: z.number(), // 家賃
  customerTransferFeeRate: z.number(), // 送客手数料率(税込)
  totalReimbursement: z.number(), // 立替元本
  customerTransferFee: z.number(), // 送客手数料(税込)
  monthlyTransferCount: z.optional(z.number()), // 同月の総客数
  exDiscountedCustomerTransferFeeRate: z.optional(z.number()), // 割引後送客手数料率(税抜)
  customerTransferFeeRateResult: z.number(), // 送客手数料率(税込)
  totalReimbursementResult: z.number(), // 立替元本
  selfPaymentResult: z.number(), // 自己負担額
  customerTransferFeeResult: z.number(), // 送客手数料(税込)
  remittanceResult: z.number(), // 立替金送金額
  exCustomerTransferFeeRateResult: z.optional(z.number()), // 送客手数料率(税抜)
})

export type CalculationSchemaKeys = keyof z.infer<typeof calculationSchema>

export type CalculationSchema = z.infer<typeof calculationSchema>

export const urlSchema = z.object({
  meta: z.optional(
    z.object({
      applicationId: z.optional(z.string()),
      userId: z.optional(z.string()),
      userName: z.optional(z.string()),
      storeId: z.optional(z.string()),
      storeName: z.optional(z.string()),
      adPrice: z.optional(z.coerce.number()),
      isRecipient: z.optional(
        z.enum(["true", "false"], {
          invalid_type_error: "無効な値です",
        })
      ),
      nop: z.optional(
        z.coerce.number().refine((num) => numberOfPaymentsValues.includes(num), {
          message: `分割回数は${numberOfPaymentsValues.join(", ")}の内のどれかでなければいけません`,
        })
      ),
      strategy: z.optional(z.string()),
      paymentMethod: z.optional(
        z.coerce.number().refine((num) => paymentMethodsValues.includes(num), {
          message: `支払い方法は${paymentMethodsValues.join(", ")}の内のどれかでなければいけません`,
        })
      ),
      scheduledDealAt: z.optional(z.string()),
      applicatedAt: z.optional(z.string()),
    })
  ),
  calc: z.optional(
    z.object({
      customerTransferFeeRate: z.object({
        result: z.optional(z.coerce.number()),
        params: z
          .optional(
            z
              .object({
                exCustomerTransferFeeRate: z.optional(z.coerce.number()),
                consumptionTaxRate: z.optional(z.coerce.number()),
                exDiscountedCustomerTransferFeeRate: z.optional(z.coerce.number()),
              })
              .or(z.string()) // [object Object]の場合があるのでstringも許容
          )
          .transform((val) => {
            if (typeof val === "string") {
              // string の場合にデフォルト値を設定
              return undefined
            }
            return val // object の場合はそのまま
          }),
      }),
      totalReimbursement: z.object({
        result: z.optional(z.coerce.number()),
        params: z.object({
          maxAdvancePayment: z.optional(z.coerce.number()),
          initialCost: z.optional(z.coerce.number()),
          downPayment: z.optional(z.coerce.number()),
        }),
      }),
      selfPayment: z.object({
        result: z.optional(z.coerce.number()),
        params: z.object({
          initialCost: z.optional(z.coerce.number()),
          maxAdvancePayment: z.optional(z.coerce.number()),
          downPayment: z.optional(z.coerce.number()),
        }),
      }),
      customerTransferFee: z.object({
        result: z.optional(z.coerce.number()),
        params: z.object({
          rent: z.optional(z.coerce.number()),
          customerTransferFeeRate: z.optional(z.coerce.number()),
        }),
      }),
      remittance: z.optional(
        z.object({
          result: z.optional(z.coerce.number()),
          params: z.object({
            totalReimbursement: z.optional(z.coerce.number()),
            customerTransferFee: z.optional(z.coerce.number()),
          }),
        })
      ),
      exCustomerTransferFeeRate: z.optional(
        z.object({
          result: z.optional(z.coerce.number()),
          params: z.optional(
            z.object({
              monthlyTransferCount: z.optional(z.coerce.number()),
            })
          ),
        })
      ),
    })
  ),
})

export type UrlSchema = z.infer<typeof urlSchema>
